import arrayMutators from 'final-form-arrays';
import setFieldData from 'final-form-set-field-data';
import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { ConnectedProps, connect } from 'react-redux';
import { useNavigate } from 'react-router';

import { ExpertRequestType } from '@/__generated__/graphql';
import { notify, popup } from '@/actions/ui';
import {
  MIN_HOURS_NOTICE_WITHOUT_ER,
  calculateExpertCredits,
  getEngagementType,
  getSuggestedTimes,
  isDurationEmpty,
  minimumTimeNotice,
  validate,
} from '@/consultation';
import { requestConsultation } from '@/consultation/store';
import { ERROR_CODES, apiErrorMessage, hasErrorCode } from '@/core/api';
import {
  EXPERT_REQUEST_NOT_ACTIVE_MESSAGE,
  UNREGISTERED_EXPERT_REQUEST_MESSAGE,
} from '@/core/apiErrorMessages';
import { money } from '@/core/money';
import { getUserOrGroup, isGroupContext } from '@/core/user';
import { RootState } from '@/store';

import TeamAccountPromoDialog from '../TeamAccountPromo/TeamAccountPromoDialog';
import RequestConsultationDialog, { FormData } from './Dialog';
import { ERR_NOT_ENOUGH_CREDITS, ERR_NO_BILLING_ACCOUNT, sharedRefusalCopy } from './constants';
import minimumTimeNoticePopup from './minimumTimeNoticePopup';

interface RequestConsultationProps {
  profile: any;
  returnTo?: string;
  expertId: string;
  onClose?: () => void;
  open: boolean;
  expertRequest?: any;
  groupId?: string;
}

/**
 * ENG-5504 (F6): resolve the credit-purchase link for the "not enough credits"
 * resolution CTA against the ACTIVE view-as context only.
 * - Group context with a billing account -> that group's purchase page.
 * - Group context whose account is unavailable (e.g. a non-member group) ->
 *   undefined, so the caller HIDES the CTA rather than falling back to another group.
 * - Personal (non-group) context -> the user's own billing page.
 */
export function resolvePurchaseLink(
  isActiveGroupContext: boolean,
  activeGroup: { slug?: string | null; billing_account?: unknown } | null | undefined
): string | undefined {
  if (!isActiveGroupContext) return '/settings/billing/purchase';
  return activeGroup?.billing_account
    ? `/team/${activeGroup.slug}/settings/credits/purchase`
    : undefined;
}

const connector = connect(
  (state: RootState) => {
    const { viewer } = state;
    const { userContext } = state.ui;
    // ENG-5504 (F6): the billing-issue resolution link must follow the ACTIVE
    // view-as context (state.ui.userContext), not the incoming groupId prop.
    // activeGroup is undefined for non-member groups (getUserOrGroup can't find
    // them in viewer.groups) and for personal (non-group) contexts.
    const isActiveGroupContext = isGroupContext(userContext);
    const activeGroup = isActiveGroupContext ? getUserOrGroup(userContext, viewer) : undefined;
    return {
      viewer,
      activeGroup,
      isActiveGroupContext,
    };
  },
  {
    requestConsultation,
    notify,
    popup,
  }
);

const RequestConsultation = ({
  profile,
  returnTo,
  expertId,
  onClose,
  open,
  expertRequest,
  groupId,

  // Redux
  viewer,
  activeGroup,
  isActiveGroupContext,
  requestConsultation,
  notify,
  popup,
}: RequestConsultationProps & ConnectedProps<typeof connector>) => {
  const navigate = useNavigate();
  const handleClose = useCallback(() => {
    if (returnTo) navigate(returnTo, { replace: true });
    if (onClose) onClose();
  }, [navigate, onClose, returnTo]);

  const purchasePromoPopup = useCallback(
    (popupFn: any, purchaseLink: any, isTeam: any) => {
      return popupFn({
        title: 'Not enough credits',
        contents: (
          <>
            There are not enough credits to request the consultation.
            {isTeam && (
              <>
                <br />
                You can purchase more credits in your team settings.
              </>
            )}
          </>
        ),
        buttonAlignment: 'space-between',
        buttons: [
          {
            label: 'Cancel',
            callback: () => {},
            flat: true,
          },
          // {
          //   label: 'Contact',
          //   primary: true,
          //   callback: () => window.HubSpotConversations?.widget?.open(),
          // },
          // ENG-5504 (F6): hide the resolution CTA when the active context has no
          // reachable purchase page (non-member group / unavailable account),
          // rather than defaulting to another group.
          ...(purchaseLink
            ? [
                {
                  label: 'Purchase Credits',
                  primary: true,
                  callback: () => navigate(purchaseLink),
                },
              ]
            : []),
        ],
      });
    },
    [navigate]
  );

  const handleSubmit = useCallback(
    (values: FormData) =>
      new Promise<void>((resolve, reject) => {
        const dates = values.dates.filter(Boolean);

        (async () => {
          try {
            const {
              duration,
              engagement_type: engagementType,
              booking_fee: bookingFee,
              ...other
            } = values;

            const expertCredits = calculateExpertCredits(
              profile.credit_rate,
              duration,
              engagementType
            );
            const cents = expertCredits + bookingFee;

            const consultation = await requestConsultation({
              ...other,
              duration,
              engagement_type: engagementType,
              expert_id: expertId,
              credits: money({ cents, currency: 'OFC' }),
              dates,
            });

            notify('Your request has been submitted.', 'success');

            navigate(`/consultation/${consultation.id}`);

            resolve();
          } catch (err: any) {
            if (err.message === ERR_NOT_ENOUGH_CREDITS || err.message === ERR_NO_BILLING_ACCOUNT) {
              // if (viewer.admin) {
              //   return resolve({
              //     group_id: 'Team does not have enough credits',
              //   });
              // }
              // ENG-5504 (F6): resolve credits against the ACTIVE view-as context
              // only, never falling back to another group (see resolvePurchaseLink).
              const purchaseLink = resolvePurchaseLink(isActiveGroupContext, activeGroup);
              purchasePromoPopup(popup, purchaseLink, !!purchaseLink && isActiveGroupContext);
              return resolve();
            }

            const refusal = sharedRefusalCopy(err);
            if (refusal) {
              notify(refusal, 'error');
              return resolve();
            }

            if (hasErrorCode(err, ERROR_CODES.EXPERT_REQUEST_NOT_ACTIVE)) {
              notify(EXPERT_REQUEST_NOT_ACTIVE_MESSAGE, 'error');
              return resolve();
            }

            // Same as the SMB flow: an outcome to explain, not a failure. The api has
            // already emailed the expert, and every retry emails them again.
            if (hasErrorCode(err, ERROR_CODES.CONSULTATION_UNREGISTERED_EXPERT_REQUEST)) {
              notify(UNREGISTERED_EXPERT_REQUEST_MESSAGE, 'warning');
              return resolve();
            }

            if (err.message === 'User not logged in') {
              notify('User not logged in.', 'error');
            } else {
              notify(
                apiErrorMessage(err, 'An error occurred when trying to submit your request.'),
                'error'
              );
            }
            reject(err);
          }
        })();
      }),
    [
      activeGroup,
      expertId,
      isActiveGroupContext,
      navigate,
      notify,
      popup,
      profile.credit_rate,
      purchasePromoPopup,
      requestConsultation,
    ]
  );

  const handleSubmitWithCheck = useCallback(
    (values: any) => {
      if (values.expert_request_id) {
        // ER attached: existing 12h soft popup (bypassable)
        const dates = values.dates.filter(Boolean);
        if (minimumTimeNotice(dates)) {
          handleSubmit(values);
        } else {
          minimumTimeNoticePopup(popup, false, () => handleSubmit(values));
        }
      } else {
        // No ER: field validation guarantees 48h compliance, submit directly
        handleSubmit(values);
      }
      if (onClose) onClose();
    },
    [handleSubmit, popup, onClose]
  );

  const initialValues = useMemo(() => {
    const commonValues = {
      expert_request_id: expertRequest?.id || '',
      group_id: groupId || '',
      requester_id: '',
      disclosure: 'full',
      engagement_type: getEngagementType(expertRequest),
      booking_fee: 0,
    };

    return expertRequest?.er_type === ExpertRequestType.WrittenReview
      ? {
          ...commonValues,
          dates: [moment().add(7, 'd').startOf('day')],
          duration:
            (!isDurationEmpty(expertRequest.expected_duration) &&
              expertRequest.expected_duration) ||
            '1h',
        }
      : {
          ...commonValues,
          dates: getSuggestedTimes({
            t1: viewer.timezone,
            t2: profile.timezone,
            hourOffset: expertRequest ? 12 : MIN_HOURS_NOTICE_WITHOUT_ER,
            hourInterval: 1,
            skipWeekends: !expertRequest,
          }),
          duration: '1h',
        };
  }, [expertRequest, groupId, profile.timezone, viewer.timezone]);

  if (!viewer.groups || viewer.groups.length === 0) {
    return (
      <TeamAccountPromoDialog
        open={open}
        onClose={handleClose}
        showEventTrack="promo.show.team_account.consultation_request"
        clickEventTrack="promo.click.team_account.consultation_request"
      />
    );
  }

  return (
    <Form<FormData>
      initialValues={initialValues}
      onSubmit={handleSubmitWithCheck}
      // When the ER dropdown is cleared mid-form, dates < 48h instantly show errors.
      // Accepted trade-off: clearing an ER after opening from one is rare, and the
      // inline error messages are self-explanatory. See ENG-5156 review board notes.
      validate={validate(viewer, {
        minHoursNotice: MIN_HOURS_NOTICE_WITHOUT_ER,
        erFieldName: 'expert_request_id',
      })}
      // @ts-ignore - refactor when change to formik
      mutators={{ setFieldData, ...arrayMutators }}
      // Only re-render if these parts of the form state change
      subscription={{
        submitting: true,
        invalid: true,
        values: true,
        initialValues: true,
      }}
    >
      {(props) => (
        <RequestConsultationDialog
          {...props}
          expertId={expertId}
          profile={profile}
          expertRequest={expertRequest}
          open={open}
          onClose={handleClose}
        />
      )}
    </Form>
  );
};

export default connector(RequestConsultation);
